import type { Session } from "../types/session"
import { normalizeSessionDirectory } from "./session-list-options"

export type SessionThread = {
  session: Session
  children: SessionThread[]
  depth: number
  hasChildren: boolean
  latestUpdated: number
}

export type VisibleSessionRow = {
  sessionId: string
  thread: SessionThread
  depth: number
  isLastChild: boolean
  hasChildren: boolean
  expanded: boolean
}

export type SessionFamilySort = "activity" | "name" | "worktree"

type SessionFamilyProjection = {
  matchesSession?: (session: Session) => boolean
  worktreeDirectory?: string
  sort: SessionFamilySort
  getWorktreeLabel: (directory: string) => string
}

function someSession(thread: SessionThread, predicate: (session: Session) => boolean): boolean {
  return predicate(thread.session) || thread.children.some((child) => someSession(child, predicate))
}

export function projectSessionFamilies(
  threads: SessionThread[],
  options: SessionFamilyProjection,
): SessionThread[] {
  const worktreeDirectory = normalizeSessionDirectory(options.worktreeDirectory)
  const projected = threads.filter((thread) => {
    if (options.matchesSession && !someSession(thread, options.matchesSession)) return false
    if (!worktreeDirectory) return true
    return someSession(thread, (session) => normalizeSessionDirectory(session.location?.directory) === worktreeDirectory)
  })

  return [...projected].sort((left, right) => {
    if (options.sort === "activity") {
      return right.latestUpdated - left.latestUpdated || right.session.id.localeCompare(left.session.id)
    }
    if (options.sort === "name") {
      return (left.session.title ?? "").localeCompare(right.session.title ?? "") || left.session.id.localeCompare(right.session.id)
    }
    const leftLabel = options.getWorktreeLabel(left.session.location?.directory ?? "")
    const rightLabel = options.getWorktreeLabel(right.session.location?.directory ?? "")
    return leftLabel.localeCompare(rightLabel) || (left.session.title ?? "").localeCompare(right.session.title ?? "")
  })
}

// Search rows are independent leaves: filtering, sorting and selection all use
// the session itself, without adding ancestors or inheriting descendant activity.
export function projectSessionSearchResults(
  sessions: Iterable<Session>,
  options: SessionFamilyProjection & { includeMainSessions: boolean; includeSubsessions: boolean },
): SessionThread[] {
  const rows = new Map<string, SessionThread>()
  for (const session of sessions) {
    if (session.parentId && !options.includeSubsessions) continue
    if (!session.parentId && !options.includeMainSessions) continue
    rows.set(session.id, { session, children: [], depth: 0, hasChildren: false, latestUpdated: session.time.updated })
  }
  return projectSessionFamilies([...rows.values()], options)
}

export function getSessionRootFromMap(instanceSessions: Map<string, Session>, sessionId: string, directoryOnly?: string): Session | null {
  let current = instanceSessions.get(sessionId)
  if (!current) return null
  const inScope = (session: Session) => !directoryOnly || normalizeSessionDirectory(session.location.directory) === normalizeSessionDirectory(directoryOnly)
  if (!inScope(current)) return null

  const seen = new Set<string>()
  while (current.parentId) {
    if (seen.has(current.id)) return null
    seen.add(current.id)
    const parent = instanceSessions.get(current.parentId)
    if (!parent || !inScope(parent)) return directoryOnly ? current : null
    current = parent
  }
  return current
}

export function getSessionAncestorIdsFromMap(instanceSessions: Map<string, Session>, sessionId: string): string[] {
  const ancestors: string[] = []
  const seen = new Set<string>([sessionId])
  let current = instanceSessions.get(sessionId)
  while (current?.parentId) {
    if (seen.has(current.parentId)) return []
    seen.add(current.parentId)
    const parent = instanceSessions.get(current.parentId)
    if (!parent) return []
    ancestors.push(parent.id)
    current = parent
  }
  ancestors.reverse()
  return ancestors
}

export type DescendantActivity = { permission: number; working: number; compacting: number; idle: number }

/** Per-ancestor counts; idle participates only while its status badge is visible. */
export function collectDescendantActivity(
  instanceSessions: Map<string, Session> | undefined,
  showIdleStatus: (session: Session) => boolean = () => false,
): Map<string, DescendantActivity> {
  const activity = new Map<string, DescendantActivity>()
  for (const session of instanceSessions?.values() ?? []) {
    const kind = session.pendingPermission || session.pendingForm
      ? "permission"
      : session.status === "working" || session.status === "compacting" ? session.status
        : session.status === "idle" && showIdleStatus(session) ? "idle" : null
    if (!kind) continue
    const seen = new Set([session.id])
    let parentId = session.parentId
    while (parentId && !seen.has(parentId) && instanceSessions!.has(parentId)) {
      seen.add(parentId)
      const counts = activity.get(parentId) ?? { permission: 0, working: 0, compacting: 0, idle: 0 }
      counts[kind] += 1
      activity.set(parentId, counts)
      parentId = instanceSessions!.get(parentId)!.parentId
    }
  }
  return activity
}

export function getDescendantSessionsFromMap(instanceSessions: Map<string, Session>, parentId: string): Session[] {
  const childrenByParent = new Map<string, Session[]>()
  for (const session of instanceSessions.values()) {
    if (!session.parentId) continue
    const children = childrenByParent.get(session.parentId)
    if (children) children.push(session)
    else childrenByParent.set(session.parentId, [session])
  }

  const descendants: Session[] = []
  const queue = [...(childrenByParent.get(parentId) ?? [])]
  const seen = new Set<string>([parentId])
  while (queue.length > 0) {
    const session = queue.shift()
    if (!session || seen.has(session.id)) continue
    seen.add(session.id)
    descendants.push(session)
    queue.push(...(childrenByParent.get(session.id) ?? []))
  }
  descendants.sort((a, b) => (b.time.updated ?? 0) - (a.time.updated ?? 0))
  return descendants
}

function buildThread(
  session: Session,
  childrenByParent: Map<string, Session[]>,
  depth: number,
  ancestorIds: Set<string>,
): SessionThread | null {
  if (ancestorIds.has(session.id)) return null
  const nextAncestorIds = new Set(ancestorIds)
  nextAncestorIds.add(session.id)

  const children: SessionThread[] = []
  for (const child of childrenByParent.get(session.id) ?? []) {
    const childThread = buildThread(child, childrenByParent, depth + 1, nextAncestorIds)
    if (childThread) children.push(childThread)
  }
  children.sort((a, b) => {
    if (b.latestUpdated !== a.latestUpdated) return b.latestUpdated - a.latestUpdated
    return b.session.id.localeCompare(a.session.id)
  })

  let latestUpdated = session.time.updated ?? 0
  for (const child of children) latestUpdated = Math.max(latestUpdated, child.latestUpdated)
  return { session, children, depth, hasChildren: children.length > 0, latestUpdated }
}

export function buildSessionThreadsFromMap(
  instanceSessions: Map<string, Session>,
  rootIds: string[],
  includedDescendantIds?: Set<string>,
  directoryOnly?: string,
): SessionThread[] {
  let includedIds: Set<string> | null = null
  if (includedDescendantIds) {
    includedIds = new Set(rootIds)
    for (const sessionId of includedDescendantIds) {
      includedIds.add(sessionId)
      for (const ancestorId of getSessionAncestorIdsFromMap(instanceSessions, sessionId)) includedIds.add(ancestorId)
    }
  }

  const childrenByParent = new Map<string, Session[]>()
  for (const session of instanceSessions.values()) {
    if (directoryOnly && normalizeSessionDirectory(session.location.directory) !== normalizeSessionDirectory(directoryOnly)) continue
    if (!session.parentId || (includedIds && !includedIds.has(session.id))) continue
    const children = childrenByParent.get(session.parentId)
    if (children) children.push(session)
    else childrenByParent.set(session.parentId, [session])
  }

  const threads: SessionThread[] = []
  const seenRootIds = new Set<string>()
  for (const rootId of rootIds) {
    if (seenRootIds.has(rootId)) continue
    seenRootIds.add(rootId)
    const root = instanceSessions.get(rootId)
    if (!root || getSessionRootFromMap(instanceSessions, root.id, directoryOnly)?.id !== root.id) continue
    const thread = buildThread(root, childrenByParent, 0, new Set())
    if (thread) threads.push(thread)
  }
  threads.sort((a, b) => {
    if (b.latestUpdated !== a.latestUpdated) return b.latestUpdated - a.latestUpdated
    const updatedDelta = (b.session.time.updated ?? 0) - (a.session.time.updated ?? 0)
    return updatedDelta || b.session.id.localeCompare(a.session.id)
  })
  return threads
}

export function collectVisibleSessionIds(threads: SessionThread[], expanded: Set<string> | undefined): string[] {
  const ids: string[] = []
  for (const thread of threads) {
    ids.push(thread.session.id)
    if (expanded?.has(thread.session.id)) ids.push(...collectVisibleSessionIds(thread.children, expanded))
  }
  return ids
}

export function flattenVisibleSessionThreads(
  threads: readonly SessionThread[],
  isExpanded: (sessionId: string) => boolean,
): VisibleSessionRow[] {
  const rows: VisibleSessionRow[] = []

  const append = (siblings: readonly SessionThread[], depth: number) => {
    siblings.forEach((thread, index) => {
      const hasChildren = thread.children.length > 0
      const expanded = hasChildren && isExpanded(thread.session.id)
      rows.push({
        sessionId: thread.session.id,
        thread,
        depth,
        isLastChild: index === siblings.length - 1,
        hasChildren,
        expanded,
      })
      if (expanded) append(thread.children, depth + 1)
    })
  }

  append(threads, 0)
  return rows
}

export function findSessionThread(threads: SessionThread[], sessionId: string): SessionThread | null {
  for (const thread of threads) {
    if (thread.session.id === sessionId) return thread
    const child = findSessionThread(thread.children, sessionId)
    if (child) return child
  }
  return null
}

export function collectSessionThreadIds(threads: SessionThread[]): string[] {
  const ids: string[] = []
  for (const thread of threads) {
    ids.push(thread.session.id)
    ids.push(...collectSessionThreadIds(thread.children))
  }
  return ids
}

export function sortSessionIdsDeepestFirst(instanceSessions: Map<string, Session>, sessionIds: string[]): string[] {
  return [...sessionIds].sort(
    (left, right) => getSessionAncestorIdsFromMap(instanceSessions, right).length - getSessionAncestorIdsFromMap(instanceSessions, left).length,
  )
}
